import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, Input, Textarea } from "@/components/ui/field";
import { api, errorMessage, ifMatch } from "@/services/api";
import { AddButton, ListSection } from "@/components/ui/list-section";
import { Skeleton, SkeletonLines } from "@/components/ui/skeleton";
import { useRecordParam } from "@/hooks/use-record-param";
import type { TranslationKey } from "@/constants/i18n";
import { deleteResource, listAll, listResource } from "@/utils/geo";
import { useTranslation } from "@/store/locale";
import { cn } from "@/utils/utils";
import { ConfirmationModal } from "./ConfirmationModal";

/**
 * Roles are groups. A role is a name and a list of things its holders may do;
 * the editor shows that list as a grid of plain verbs per kind of record, so
 * nobody has to know what `change__access_policy_setting` means.
 */

interface GroupSummary {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
}

interface GroupRead extends GroupSummary {
  updated_at?: string;
  permissions: { id: number; codename: string; name: string }[];
}

interface Permission {
  id: number;
  codename: string;
  name: string;
  content_type: { id: number; model: string; verbose_name: string };
}

/** The four verbs every kind of record has, in the words a person would use. */
const VERBS = [
  { action: "view", label: "roles.view", hint: "roles.viewHint" },
  { action: "add", label: "roles.add", hint: "roles.addHint" },
  { action: "change", label: "roles.change", hint: "roles.changeHint" },
  { action: "delete", label: "roles.remove", hint: "roles.removeHint" },
] as const satisfies readonly { action: string; label: TranslationKey; hint: TranslationKey }[];

const titleCase = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** Plumbing nobody grants by hand; keeping it out of the grid keeps the grid honest. */
const HIDDEN_MODELS = new Set([
  "refresh_token",
  "password_reset_token",
  "content_type",
  "permission",
]);

/** Where the product's word differs from the model's. */
const LABELS: Record<string, TranslationKey> = {
  group: "roles.labelGroup",
  user: "roles.labelUser",
  member: "roles.model.member",
  member_note: "roles.model.memberNote",
  member_attachment: "roles.model.memberAttachment",
  member_log: "roles.model.memberLog",
  member_column_setting: "roles.model.memberColumnSetting",
  member_saved_filter: "roles.model.memberSavedFilter",
  member_card_history: "roles.model.cardHistory",
  member_card_transaction: "roles.model.cardTransaction",
  member_print_history: "roles.model.printHistory",
  member_acquisition_detail: "roles.model.acquisitionDetail",
  member_amendment_detail: "roles.model.amendmentDetail",
  member_supplementary_history: "roles.model.supplementaryHistory",
  non_member: "roles.model.nonMember",
  access_policy_setting: "roles.model.accessPolicySetting",
  employee_activity_log: "roles.model.employeeActivityLog",
  organization: "roles.model.organization",
  province: "roles.model.province",
  city: "roles.model.city",
  barangay: "roles.model.barangay",
  club: "roles.model.club",
  yellow_page: "roles.model.yellowPage",
  yellow_page_category: "roles.model.yellowPageCategory",
  promo_code: "roles.model.promoCode",
  reason: "roles.model.reason",
  business_type: "roles.model.businessType",
};

/**
 * Which section of the table a model belongs to.
 *
 * Thirty models as one flat list is a wall nobody reads to the end of. Grouped,
 * a person looking for "can they edit members" opens one section of three rows
 * instead of scanning a hundred and eight checkboxes. Anything not named here
 * falls to the last group rather than disappearing, so a model added later is
 * still reachable while somebody decides where it belongs.
 */
const GROUPS: { key: TranslationKey; models: string[] }[] = [
  {
    key: "roles.groupMembers",
    models: [
      "member",
      "member_note",
      "member_attachment",
      "member_log",
      "member_column_setting",
      "member_saved_filter",
      "member_card_history",
      "member_card_transaction",
      "member_print_history",
      "member_acquisition_detail",
      "member_amendment_detail",
      "member_supplementary_history",
      "non_member",
    ],
  },
  {
    key: "roles.groupPeople",
    models: ["user", "group", "access_policy_setting", "employee_activity_log", "organization"],
  },
  { key: "roles.groupGeography", models: ["province", "city", "barangay", "club"] },
  { key: "roles.groupCatalogue", models: [] },
];
const PAGE = 10;

export function RolesSection() {
  const t = useTranslation();

  // Track the active group selected for deletion
  const [deletingGroup, setDeletingGroup] = useState<GroupSummary | null>(null);

  const queryClient = useQueryClient();

  const groups = useInfiniteQuery({
    queryKey: ["/groups", "infinite"],
    queryFn: ({ pageParam, signal }) =>
      listResource<GroupSummary>(
        "/groups",
        { page: pageParam, size: PAGE, sort: "id" },
        signal,
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
  });
  const remove = useMutation({
    mutationFn: (id: number) => deleteResource("/groups", id),
    onSuccess: () => {
      toast.success(t("roles.deleted"));
      void queryClient.invalidateQueries({ queryKey: ["/groups"] });
    },
    onError: (error) => toast.error(errorMessage(error, t("roles.deleteFailed"))),
  });

  const rows = groups.data?.pages.flatMap((page) => page.items) ?? [];
  const { editing, setEditing, loading } = useRecordParam<GroupSummary>(
    "edit",
    "role",
    "/groups",
  );

  // The role is read when it is opened; the same skeleton the editor shows
  // while it reads its own group, so the two waits are one.
  if (loading) {
    return (
      <div className="grid gap-6 py-6">
        <Skeleton className="h-9 w-72 rounded-lg" />
        <SkeletonLines rows={6} />
      </div>
    );
  }

  if (editing !== null) {
    return (
      <RoleEditor
        groupId={editing === "new" ? null : editing.id}
        onDone={() => {
          setEditing(null);
          void queryClient.invalidateQueries({ queryKey: ["/groups"] });
        }}
      />
    );
  }

  return (
    <>
      <div className="pr-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-title-2 text-heading">{t("cfg.roles")}</h2>
            <p className="text-fg-muted text-callout mt-1">{t("roles.intro")}</p>
          </div>
        </div>

        <ListSection
          className="mt-4"
          title={t("cfg.roles")}
          shown={rows.length}
          total={groups.data?.pages[0]?.total}
          refreshKeys={[["/groups"]]}
          action={<AddButton onClick={() => setEditing("new")}>{t("roles.addRole")}</AddButton>}
          hasMore={groups.hasNextPage}
          loadingMore={groups.isFetchingNextPage}
          pending={groups.isPending}
          onLoadMore={() => void groups.fetchNextPage()}
          empty={t("roles.none")}
        >
          {rows.map((group) => (
            <li key={group.id} className="flex items-center justify-between gap-4 py-4">
              <span className="min-w-0">
                <span className="text-fg text-callout flex items-center gap-2 font-medium">
                  {group.name}
                  {group.is_system ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : null}
                </span>
                {group.description ? (
                  <span className="text-fg-muted text-subhead mt-0.5 block truncate">
                    {group.description}
                  </span>
                ) : null}
              </span>
              <span className="text-callout flex shrink-0 items-center gap-5">
                <button
                  type="button"
                  onClick={() => setEditing(group)}
                  className="text-fg-muted hover:bg-surface-inset hover:text-fg rounded-lg px-2 py-1 transition-colors"
                >
                  {t("common.edit")}
                </button>
                {!group.is_system && (
                  <button
                    type="button"
                    onClick={() => setDeletingGroup(group)}
                    className="text-fg-muted hover:bg-danger-soft hover:text-danger rounded-lg px-2 py-1 transition-colors"
                  >
                    {t("common.delete")}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ListSection>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={deletingGroup !== null}
          onClose={() => setDeletingGroup(null)}
          onConfirm={() => {
            if (deletingGroup) {
              remove.mutate(deletingGroup.id);
            }
            setDeletingGroup(null);
          }}
          title={t("roles.confirmDeleteTitle")}
          message={
            deletingGroup ? t("roles.confirmDelete").replace("{name}", deletingGroup.name) : ""
          }
        />
      </div>
    </>
  );
}

function RoleEditor({ groupId, onDone }: { groupId: number | null; onDone: () => void }) {
  // The whole catalogue, however many pages: a role must be able to see every permission.
  const permissions = useQuery({
    queryKey: ["/permissions", "all"],
    queryFn: ({ signal }) => listAll<Permission>("/permissions", { sort: "codename" }, signal),
    staleTime: 5 * 60_000,
  });
  const group = useQuery({
    queryKey: ["/groups", groupId],
    queryFn: async ({ signal }) =>
      (await api.get<GroupRead>(`/groups/${groupId}`, { signal })).data,
    enabled: groupId !== null,
  });

  if (permissions.isPending || (groupId !== null && group.isPending)) {
    return (
      <div className="grid gap-6 py-6">
        <Skeleton className="h-9 w-72 rounded-lg" />
        <SkeletonLines rows={6} />
      </div>
    );
  }
  // Keyed so a different role gets a fresh form; state starts from the data.
  return (
    <RoleForm
      key={groupId ?? "new"}
      groupId={groupId}
      initial={group.data ?? null}
      permissions={(permissions.data ?? []).filter(
        (p) => !HIDDEN_MODELS.has(p.content_type.model),
      )}
      onDone={onDone}
    />
  );
}

function RoleForm({
  groupId,
  initial,
  permissions,
  onDone,
}: {
  groupId: number | null;
  initial: GroupRead | null;
  permissions: Permission[];
  onDone: () => void;
}) {
  const t = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initial?.permissions.map((p) => p.codename) ?? []),
  );
  const [error, setError] = useState<string | null>(null);
  // The first section is open; the rest wait to be asked for. Opening all four
  // at once is the flat wall this replaces.
  const [expanded, setExpanded] = useState<Set<TranslationKey>>(
    () => new Set([GROUPS[0]!.key]),
  );

  // Rows are kinds of record; each has the four verbs where they exist, and
  // any extra, hand-named permissions on their own.
  const rows = useMemo(() => {
    const byModel = new Map<string, { label: string; perms: Permission[] }>();
    for (const perm of permissions) {
      const key = perm.content_type.model;
      const entry = byModel.get(key) ?? {
        label:
          (LABELS[key] ? t(LABELS[key]) : undefined) ??
          titleCase(perm.content_type.verbose_name),
        perms: [],
      };
      entry.perms.push(perm);
      byModel.set(key, entry);
    }
    return [...byModel.entries()]
      .map(([model, { label, perms }]) => ({
        model,
        label,
        verbs: VERBS.map(
          (verb) => perms.find((p) => p.codename === `${verb.action}__${model}`) ?? null,
        ),
        extras: perms.filter(
          (p) => !VERBS.some((verb) => p.codename === `${verb.action}__${model}`),
        ),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [permissions, t]);

  /** The same rows, folded into the sections of the table. */
  const sections = useMemo(() => {
    const placed = new Set(GROUPS.flatMap((group) => group.models));
    return GROUPS.map((group) => ({
      key: group.key,
      // The last group has no list of its own: it takes whatever is left, so a
      // model added to the API still appears without a change here.
      rows: rows.filter((row) =>
        group.models.length > 0 ? group.models.includes(row.model) : !placed.has(row.model),
      ),
    })).filter((section) => section.rows.length > 0);
  }, [rows]);

  const all = useMemo(() => permissions.map((p) => p.codename), [permissions]);
  const viewing = useMemo(() => all.filter((c) => c.startsWith("view__")), [all]);
  const toggle = (codenames: string[], on: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      for (const code of codenames)
        if (on) next.add(code);
        else next.delete(code);
      return next;
    });
  const has = (codenames: string[]) =>
    codenames.length > 0 && codenames.every((c) => selected.has(c));

  const save = useMutation({
    mutationFn: (body: { name: string; description: string | null; codenames: string[] }) =>
      groupId === null
        ? api.post("/groups", body)
        : api.patch(`/groups/${groupId}`, body, ifMatch(initial)),
    onSuccess: () => {
      toast.success(groupId === null ? t("roles.created") : t("roles.updated"));
      onDone();
    },
    onError: (err) => setError(errorMessage(err, t("roles.saveFailed"))),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    save.mutate({
      name: name.trim(),
      description: description.trim() || null,
      codenames: [...selected],
    });
  };

  const isSystem = initial?.is_system ?? false;

  return (
    <form onSubmit={submit}>
      <button
        type="button"
        onClick={onDone}
        className="text-fg-muted hover:text-fg text-callout inline-flex items-center gap-2"
      >
        <ArrowLeft className="size-4" aria-hidden /> {t("roles.back")}
      </button>
      <h2 className="text-title-2 text-heading mt-3">
        {groupId === null ? t("roles.new") : t("roles.edit")}
      </h2>

      {/* Two sections: who the role is, then what it may do. They were one run
          of controls down the page, with the sentence explaining the table five
          elements above it. A name is a few words and a description is a
          sentence or two, so the description gets its own line. */}
      {/* The card runs the page's width so its edges meet the table below;
          the fields keep a readable measure and the count sits in the space
          that was otherwise left blank on the right. */}
      <section className="bg-surface-raised rounded-card mt-5 p-5 shadow-[var(--shadow-lift)]">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
          <h3 className="text-heading text-title-3">{t("roles.details")}</h3>
          {isSystem ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : null}
        </div>
        {/* The fields take the card's width. Held to a column they left the
            right half of a wide screen blank, and the description — which is
            the one field here that can run long — had the least room of all. */}
        <div className="grid gap-5">
          <Field
            label={t("roles.name")}
            htmlFor="role-name"
            hint={isSystem ? t("roles.builtInName") : undefined}
          >
            <Input
              id="role-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSystem}
              placeholder={t("roles.namePlaceholder")}
            />
          </Field>
          <Field label={t("roles.description")} htmlFor="role-description">
            <Textarea
              id="role-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("roles.descriptionPlaceholder")}
            />
          </Field>
        </div>
      </section>

      <section className="bg-surface-raised rounded-card mt-4 shadow-[var(--shadow-lift)]">
        <div className="hairline-b p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="text-heading text-title-3">{t("roles.permissions")}</h3>
            <span className="text-fg-muted text-callout tabular-nums">
              {t("roles.count")
                .replace("{selected}", String(selected.size))
                .replace("{total}", String(all.length))}
            </span>
          </div>
          <p className="text-fg-muted text-callout mt-1">{t("roles.tickIntro")}</p>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-fg-muted text-callout mr-1">{t("roles.quickStart")}</span>
            {/* Toggles, not one-way switches. They only ever added, so once a
                set was on the button did nothing and the only way back was
                Clear all — which reads as the button being broken. Pressed
                is the state, so it also says what is currently true. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={has(viewing)}
              className={cn(has(viewing) && "border-accent text-accent bg-accent-soft")}
              onClick={() => toggle(viewing, !has(viewing))}
            >
              {t("roles.seeEverything")}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={has(all)}
              className={cn(has(all) && "border-accent text-accent bg-accent-soft")}
              onClick={() => toggle(all, !has(all))}
            >
              {t("roles.doEverything")}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              {t("roles.clearAll")}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="text-callout w-full">
            <thead className="bg-surface-sunken text-fg-muted text-subhead text-left">
              <tr>
                <th className="px-4 py-3">{t("roles.kindOfRecord")}</th>
                {VERBS.map((verb) => {
                  const column = rows
                    .map((r) => r.verbs[VERBS.indexOf(verb)]?.codename)
                    .filter((c): c is string => Boolean(c));
                  return (
                    <th key={verb.action} className="px-3 py-3 text-center">
                      <label className="inline-flex cursor-pointer flex-col items-center gap-1">
                        <span title={t(verb.hint)}>{t(verb.label)}</span>
                        <input
                          type="checkbox"
                          aria-label={t("roles.forEvery").replace("{verb}", t(verb.label))}
                          className="accent-accent size-4"
                          checked={has(column)}
                          onChange={(e) => toggle(column, e.target.checked)}
                        />
                      </label>
                    </th>
                  );
                })}
              </tr>
            </thead>
            {sections.map((section) => {
              const codes = section.rows.flatMap((row) =>
                [...row.verbs, ...row.extras]
                  .filter((p): p is Permission => p !== null)
                  .map((p) => p.codename),
              );
              const chosen = codes.filter((code) => selected.has(code)).length;
              const open = expanded.has(section.key);
              return (
                <tbody key={section.key} className="divide-line divide-y">
                  {/* The heading row carries the same columns the rows below it
                      do, so "view everything about members" is one tick in the
                      View column of this row rather than fifty. */}
                  <tr className="bg-surface-sunken/60">
                    <th className="p-0 text-left font-normal">
                      <div className="flex items-center gap-3 px-4 py-2.5">
                        <button
                          type="button"
                          aria-expanded={open}
                          onClick={() =>
                            setExpanded((was) => {
                              const next = new Set(was);
                              if (!next.delete(section.key)) next.add(section.key);
                              return next;
                            })
                          }
                          className="text-fg text-callout inline-flex min-w-0 items-center gap-2 font-medium"
                        >
                          <ChevronRight
                            className={cn(
                              "size-4 shrink-0 transition-transform",
                              open && "rotate-90",
                            )}
                            aria-hidden
                          />
                          {t(section.key)}
                          {/* Beside the name, not across the row: how much of a
                              group is granted belongs to the group's label. */}
                          <span className="text-fg-muted text-footnote shrink-0 tabular-nums">
                            {t("roles.groupCount")
                              .replace("{selected}", String(chosen))
                              .replace("{total}", String(codes.length))}
                          </span>
                        </button>
                      </div>
                    </th>
                    {VERBS.map((verb, index) => {
                      // Only the rows that have this action: `Member` has no
                      // delete, so ticking Delete here must not claim it does.
                      const column = section.rows
                        .map((row) => row.verbs[index]?.codename)
                        .filter((code): code is string => Boolean(code));
                      return (
                        <td key={verb.action} className="px-3 py-2.5 text-center">
                          {column.length ? (
                            <input
                              type="checkbox"
                              aria-label={`${t(verb.label)} — ${t(section.key)}`}
                              className="accent-accent size-4"
                              checked={has(column)}
                              onChange={(event) => toggle(column, event.target.checked)}
                            />
                          ) : (
                            <span className="text-fg-subtle">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {open
                    ? section.rows.map((row) => (
                        <tr
                          key={row.model}
                          className="hover:bg-surface-sunken transition-colors"
                        >
                          {/* Indented, and with a rule down the left: these
                                belong to the heading above them, which a flat
                                list gave no way to tell. */}
                          <td className="text-fg py-3 pr-4 pl-4 font-medium">
                            <span className="border-line ml-2 block border-l pl-5">
                              {row.label}
                              {row.extras.length ? (
                                <div className="mt-2 grid gap-1.5">
                                  {row.extras.map((perm) => (
                                    <label
                                      key={perm.codename}
                                      className="text-fg-muted text-subhead inline-flex cursor-pointer items-center gap-2 font-normal"
                                    >
                                      <input
                                        type="checkbox"
                                        className="accent-accent size-4"
                                        checked={selected.has(perm.codename)}
                                        onChange={(e) =>
                                          toggle([perm.codename], e.target.checked)
                                        }
                                      />
                                      {perm.name}
                                    </label>
                                  ))}
                                </div>
                              ) : null}
                            </span>
                          </td>
                          {row.verbs.map((perm, index) => (
                            <td
                              key={VERBS[index]?.action}
                              className="px-3 py-3 text-center align-top"
                            >
                              {perm ? (
                                <input
                                  type="checkbox"
                                  aria-label={`${VERBS[index]?.label} ${row.label}`}
                                  className="accent-accent size-4"
                                  checked={selected.has(perm.codename)}
                                  onChange={(e) => toggle([perm.codename], e.target.checked)}
                                />
                              ) : (
                                <span className="text-fg-subtle">—</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    : null}
                </tbody>
              );
            })}
            {rows.length === 0 ? (
              <tbody>
                <tr>
                  <td
                    colSpan={VERBS.length + 2}
                    className="text-fg-muted px-4 py-10 text-center"
                  >
                    {t("roles.noPermissions")}
                  </td>
                </tr>
              </tbody>
            ) : null}
          </table>
        </div>
      </section>

      {error ? (
        <p role="alert" className="text-danger text-callout mt-4">
          {error}
        </p>
      ) : null}

      <div className={cn("mt-6 flex items-center justify-end gap-3")}>
        <Button type="button" variant="outline" onClick={onDone}>
          {t("common.cancel")}
        </Button>
        <Button type="submit" disabled={save.isPending || name.trim() === ""}>
          <ShieldCheck />{" "}
          {save.isPending
            ? t("common.saving")
            : groupId === null
              ? t("roles.createRole")
              : t("roles.saveRole")}
        </Button>
      </div>
    </form>
  );
}
